import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { usePermissions } from '../../shared/permissions';
import { useConfirm } from '../../shared/ConfirmContext';
import StatusBadge from '../../components/StatusBadge';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';

const VehicleTariffTypeManagement = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const queryClient = useQueryClient();

  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const emptyForm = { name: '', code: '', description: '', unit: '', color: '#4dabf7', is_active: true, order: 0 };
  const [formData, setFormData] = useState(emptyForm);

  const { data: tariffTypes = [], isLoading: loading } = useQuery({
    queryKey: ['vehicle-tariff-types'],
    queryFn: async () => {
      const response = await api.get('transport/api/vehicle-tariff-types/');
      const data = Array.isArray(response) ? response : (response.results || []);
      return data.sort((a, b) => (a.order || 0) - (b.order || 0));
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['vehicle-tariff-types'] });

  const addMutation = useMutation({
    mutationFn: (payload) => api.post('transport/api/vehicle-tariff-types/', payload),
    onSuccess: () => { setShowAddModal(false); setFormData(emptyForm); invalidate(); },
    onError: () => setError(t('transport.tariffTypes.errors.failedToCreate')),
  });

  const editMutation = useMutation({
    mutationFn: (payload) => api.put(`transport/api/vehicle-tariff-types/${selectedType.id}/`, payload),
    onSuccess: () => { setShowEditModal(false); setSelectedType(null); setFormData(emptyForm); invalidate(); },
    onError: () => setError(t('transport.tariffTypes.errors.failedToUpdate')),
  });

  const deleteMutation = useMutation({
    mutationFn: (typeId) => api.delete(`transport/api/vehicle-tariff-types/${typeId}/`),
    onSuccess: () => invalidate(),
    onError: () => setError(t('transport.tariffTypes.errors.failedToDelete')),
  });

  const reorderMutation = useMutation({
    mutationFn: (reordered) => Promise.all(
      reordered.map((item, index) =>
        api.patch(`transport/api/vehicle-tariff-types/${item.id}/`, { order: (index + 1) * 10 })
      )
    ),
    onSettled: () => invalidate(),
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAdd = (e) => {
    e.preventDefault();
    addMutation.mutate(formData);
  };

  const handleEdit = (e) => {
    e.preventDefault();
    editMutation.mutate(formData);
  };

  const handleDelete = async (typeId) => {
    if (!await confirm(t('transport.tariffTypes.confirmDelete'))) return;
    deleteMutation.mutate(typeId);
  };

  const openEditModal = (tariffType) => {
    setSelectedType(tariffType);
    setFormData({
      name: tariffType.name || '',
      code: tariffType.code || '',
      description: tariffType.description || '',
      unit: tariffType.unit || '',
      color: tariffType.color || '#4dabf7',
      is_active: tariffType.is_active !== false,
      order: tariffType.order || 0,
    });
    setShowEditModal(true);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const reordered = Array.from(tariffTypes);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    queryClient.setQueryData(['vehicle-tariff-types'], reordered);
    reorderMutation.mutate(reordered);
  };

  const formBody = (
    <div className="modal-body">
      <div className="row">
        <div className="col-md-8 mb-3">
          <label className="form-label required">{t('transport.tariffTypes.name')}</label>
          <input
            type="text"
            className="form-control"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder={t('transport.tariffTypes.namePlaceholder')}
            required
          />
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-label required">{t('transport.tariffTypes.code')}</label>
          <input
            type="text"
            className="form-control"
            name="code"
            value={formData.code}
            onChange={handleInputChange}
            placeholder={t('transport.tariffTypes.codePlaceholder')}
            required
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-8 mb-3">
          <label className="form-label required">{t('transport.tariffTypes.unit')}</label>
          <input
            type="text"
            className="form-control"
            name="unit"
            value={formData.unit}
            onChange={handleInputChange}
            placeholder={t('transport.tariffTypes.unitPlaceholder')}
            required
          />
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-label">{t('transport.tariffTypes.order')}</label>
          <input
            type="number"
            className="form-control"
            name="order"
            value={formData.order}
            onChange={handleInputChange}
            min="0"
          />
        </div>
      </div>
      <div className="mb-3">
        <label className="form-label">{t('transport.tariffTypes.description')}</label>
        <textarea
          className="form-control"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows="2"
          placeholder={t('transport.tariffTypes.descriptionPlaceholder')}
        />
      </div>
      <div className="row align-items-end">
        <div className="col-auto mb-3">
          <label className="form-label">{t('transport.tariffTypes.color')}</label>
          <input
            type="color"
            className="form-control form-control-color"
            name="color"
            value={formData.color}
            onChange={handleInputChange}
            title={t('common.pickColor')}
          />
        </div>
        <div className="col mb-3">
          <div className="form-check form-switch mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              name="is_active"
              id="isActiveCheck"
              checked={formData.is_active}
              onChange={handleInputChange}
            />
            <label className="form-check-label" htmlFor="isActiveCheck">
              {t('transport.tariffTypes.isActive')}
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <PageShell
      pretitle={t('transport.title')}
      title={t('transport.tariffTypes.title')}
      actions={(
        <>
          <Link to="/transport" className="btn btn-outline-secondary d-none d-sm-inline-block">
            <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <path d="M5 12l14 0"></path>
              <path d="M5 12l6 6"></path>
              <path d="M5 12l6 -6"></path>
            </svg>
            {t('common.back')}
          </Link>
          {canManage && (
            <button className="btn btn-primary" onClick={() => { setFormData(emptyForm); setShowAddModal(true); }}>
              <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M12 5l0 14"/>
                <path d="M5 12l14 0"/>
              </svg>
              {t('transport.tariffTypes.add')}
            </button>
          )}
        </>
      )}
    >
      {error && (
        <div className="alert alert-danger alert-dismissible mb-0" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {loading ? (
        <PageSection>
          <CenteredLoader />
        </PageSection>
      ) : tariffTypes.length === 0 ? (
        <PageSection>
          <div className="empty">
            <div className="empty-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2"></path>
                <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z"></path>
              </svg>
            </div>
            <p className="empty-title">{t('transport.tariffTypes.title')}</p>
            <p className="empty-subtitle text-muted">{t('transport.tariffTypes.noTypes')}</p>
            <div className="empty-action">
              <button className="btn btn-primary" onClick={() => { setFormData(emptyForm); setShowAddModal(true); }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M12 5l0 14"/>
                  <path d="M5 12l14 0"/>
                </svg>
                {t('transport.tariffTypes.add')}
              </button>
            </div>
          </div>
        </PageSection>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="card">
                <div className="card-header">
                  <h3 className="card-title">{t('transport.tariffTypes.title')}</h3>
                  <div className="card-options">
                    <small className="text-muted">{t('transport.tariffTypes.dragHint')}</small>
                  </div>
                </div>
                <Droppable droppableId="vehicle-tariff-types">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="card-body p-0"
                      style={{ minHeight: '100px', maxHeight: '70vh', overflowY: 'auto' }}
                    >
                      {tariffTypes.map((tariffType, index) => (
                        <Draggable
                          key={`vtt-${tariffType.id}`}
                          draggableId={`vtt-${tariffType.id}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className="border-bottom p-3 d-flex align-items-center"
                              style={{
                                ...provided.draggableProps.style,
                                backgroundColor: snapshot.isDragging ? 'var(--tblr-bg-surface-secondary)' : '',
                                borderLeft: snapshot.isDragging ? '4px solid var(--tblr-primary)' : '4px solid transparent',
                                boxShadow: snapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                              }}
                            >
                              {/* Drag handle */}
                              <div
                                {...provided.dragHandleProps}
                                className="me-3 text-muted d-flex align-items-center"
                                style={{ cursor: snapshot.isDragging ? 'grabbing' : 'grab' }}
                                title={t('common.dragToReorder')}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                  <circle cx="9" cy="12" r="1"></circle>
                                  <circle cx="9" cy="7" r="1"></circle>
                                  <circle cx="9" cy="17" r="1"></circle>
                                  <circle cx="15" cy="12" r="1"></circle>
                                  <circle cx="15" cy="7" r="1"></circle>
                                  <circle cx="15" cy="17" r="1"></circle>
                                </svg>
                              </div>

                              {/* Order badge */}
                              <div className="me-3">
                                <span className="badge bg-secondary">{tariffType.order}</span>
                              </div>

                              {/* Color swatch */}
                              <div
                                className="me-3 flex-shrink-0"
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  backgroundColor: tariffType.color || '#4dabf7',
                                  borderRadius: '4px',
                                  border: '2px solid rgba(0,0,0,0.1)',
                                }}
                              />

                              {/* Info */}
                              <div className="flex-grow-1 min-w-0">
                                <div className="d-flex align-items-center gap-2">
                                  <span className="fw-semibold">{tariffType.name}</span>
                                  <StatusBadge color={tariffType.color} label={tariffType.code} />
                                  {!tariffType.is_active && (
                                    <span className="badge bg-secondary">{t('transport.tariffTypes.inactive')}</span>
                                  )}
                                </div>
                                <div className="text-muted small">
                                  {t('transport.tariffTypes.unit')}: <strong>{tariffType.unit}</strong>
                                  {tariffType.description && <span className="ms-2">— {tariffType.description}</span>}
                                </div>
                              </div>

                              {/* Actions */}
                              {canManage && (
                                <div className="ms-3 flex-shrink-0">
                                  <div className="btn-group">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={(e) => { e.stopPropagation(); openEditModal(tariffType); }}
                                      title={t('common.edit')}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"></path>
                                        <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"></path>
                                        <path d="M16 5l3 3"></path>
                                      </svg>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={(e) => { e.stopPropagation(); handleDelete(tariffType.id); }}
                                      title={t('common.delete')}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M4 7l16 0"></path>
                                        <path d="M10 11l0 6"></path>
                                        <path d="M14 11l0 6"></path>
                                        <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                                        <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
          </div>
        </DragDropContext>
      )}

      {showAddModal && (
        <div className="modal modal-blur show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <form onSubmit={handleAdd} className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('transport.tariffTypes.add')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
              </div>
              {formBody}
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setShowAddModal(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary ms-auto">
                  {t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedType && (
        <div className="modal modal-blur show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <form onSubmit={handleEdit} className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('transport.tariffTypes.edit')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              {formBody}
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setShowEditModal(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary ms-auto">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default VehicleTariffTypeManagement;
